import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Mic, Drama, SlidersHorizontal, Headphones, ArrowRight, Mail, MapPin, Star, ExternalLink, Download, Megaphone } from "lucide-react";
import { FaInstagram, FaTiktok, FaThreads, FaFacebookF } from "react-icons/fa6";
import { api, LOGO_FULL, resolveAudio, optimizeImg } from "../lib/api";
import { stopAllMediaExcept } from "../lib/mediaBus";
import { Navbar } from "../components/Navbar";
import { AudioPlayer } from "../components/AudioPlayer";
import { BeforeAfter } from "../components/BeforeAfter";
import { QuoteDialog } from "../components/QuoteDialog";
import { MusicSection } from "../components/MusicSection";
import { VoiceFXLab } from "../components/VoiceFXLab";
import { HowItWorksButton } from "../components/HowItWorks";
import { Button } from "../components/ui/button";

const ICONS = { Mic, Drama, SlidersHorizontal, Headphones, Megaphone };

const HERO_BG = "https://images.unsplash.com/photo-1531651008558-ed1740375b39?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHx2b2ljZSUyMGFjdG9yJTIwcmVjb3JkaW5nJTIwYm9vdGh8ZW58MHx8fHwxNzgyODU1OTc4fDA&ixlib=rb-4.1.0&q=85";

const CATS = [
  { key: "narration", label: "Narrations" },
  { key: "voice", label: "Character Voices" },
  { key: "challenge", label: "Challenges" },
  { key: "teaser", label: "Teasers" },
  { key: "audiobook", label: "Audiobooks" },
];
const SHOW_REQUEST = ["narration", "voice", "challenge", "teaser"];
const CATEGORY_SERVICE = {
  narration: "Voice Over Narration",
  audiobook: "Voice Over Narration",
  voice: "Voice Acting",
  challenge: "Voice Acting",
  teaser: "Social Media Promos & Book Teasers",
};

const SOCIALS = [
  { key: "instagram", label: "Instagram", Icon: FaInstagram, url: "https://www.instagram.com/e.o.vo/" },
  { key: "tiktok", label: "TikTok", Icon: FaTiktok, url: "https://www.tiktok.com/@e.o.vo.tt?_r=1&_t=ZS-987j5BiylbZ" },
  { key: "threads", label: "Threads", Icon: FaThreads, url: "https://www.threads.com/@e.o.vo" },
  { key: "facebook", label: "Facebook", Icon: FaFacebookF, url: "https://www.facebook.com/share/1BRwGwWCct/" },
  { key: "acx", label: "ACX", text: "acx", url: "https://www.acx.com/narrator?p=A24FO3KFAYQZ0C" },
];

const SocialLinks = ({ testidPrefix = "hero-social" }) => (
  <div className="flex items-center gap-3" data-testid="hero-socials">
    {SOCIALS.map(({ key, label, Icon, text, url }) => (
      <a key={key} href={url} target="_blank" rel="noopener noreferrer" aria-label={label} title={label}
        data-testid={`${testidPrefix}-${key}`}
        className="h-10 w-10 rounded-full border border-white/15 bg-white/[0.03] text-[#D4D4D8] flex items-center justify-center hover:border-[#C8A97E] hover:text-[#C8A97E] hover:bg-[#C8A97E]/10 hover:-translate-y-0.5 transition-all duration-200">
        {text ? <span className="font-black text-[13px] lowercase tracking-tighter">{text}</span> : <Icon size={17} />}
      </a>
    ))}
  </div>
);

const Reveal = ({ children, delay = 0 }) => (
  <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.6, delay }}>{children}</motion.div>
);

const ScriptExcerpt = ({ text, testid }) => {
  const [open, setOpen] = useState(false);
  const long = text.length > 160;
  return (
    <div className="border-l-2 border-[#C8A97E]/40 pl-3">
      <p className={`text-xs italic text-[#8b8b93] leading-relaxed ${open || !long ? "" : "line-clamp-2"}`} data-testid={testid}>"{text}"</p>
      {long && (
        <button onClick={() => setOpen(!open)} data-testid={`${testid}-toggle`}
          className="text-[11px] font-mono text-[#C8A97E] hover:text-[#B3956D] mt-1 transition-colors">
          {open ? "Show less" : "Read script"}
        </button>
      )}
    </div>
  );
};

const FeaturedSpotlight = ({ book, teasers, kicker, title, sub }) => {
  if (!book) return null;
  return (
    <section id="featured" className="border-y border-white/5 bg-[#0d0e13] relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{ backgroundImage: `url(${optimizeImg(book.cover_url, 400)})`, backgroundSize: "cover", backgroundPosition: "center", filter: "blur(40px)" }} />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32">
        <Reveal>
          <div className="mb-12">
            <span className="font-mono text-xs tracking-[0.3em] text-[#C8A97E] uppercase">{kicker || "Featured Project"}</span>
            <h2 className="font-head text-4xl md:text-5xl font-bold tracking-tight mt-3">{title || "Manuscript to Marketing"}</h2>
            <p className="text-[#A1A1AA] mt-4 max-w-2xl leading-relaxed">{sub || "From immersive narration to custom social-media teasers — a complete look at an end-to-end fantasy audiobook production."}</p>
          </div>
        </Reveal>
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-4" data-testid="featured-book">
            <img src={optimizeImg(book.cover_url, 500)} alt={book.title} loading="lazy" decoding="async" className="w-full max-w-xs rounded-lg border border-white/10 shadow-2xl" />
            <h3 className="font-head text-2xl font-medium mt-6">{book.title}</h3>
            <p className="text-sm text-[#A1A1AA] mt-2 leading-relaxed">{book.description}</p>
            <div className="flex flex-wrap gap-2 mt-5">
              {(book.links || []).filter((l) => !l.label.toLowerCase().startsWith("author")).map((l, i) => (
                <a key={l.url || l.label} href={l.url} target="_blank" rel="noopener noreferrer" data-testid={`featured-link-${i}`}
                  className="inline-flex items-center gap-1.5 text-xs bg-[#C8A97E] text-[#0B0C10] font-semibold px-3 py-1.5 rounded-sm hover:bg-[#B3956D] transition-colors">
                  <ExternalLink size={13} /> {l.label}
                </a>
              ))}
            </div>
            {(book.links || []).filter((l) => l.label.toLowerCase().startsWith("author")).map((l) => (
              <a key={l.url || l.label} href={l.url} target="_blank" rel="noopener noreferrer" data-testid="featured-author"
                className="group flex items-center gap-3 mt-5 p-2.5 pr-4 rounded-full border border-white/10 hover:border-[#C8A97E]/60 bg-[#12141A] w-fit transition-colors">
                <img src="https://customer-assets.emergentagent.com/job_sonic-pro-1/artifacts/9s1fjekr_731037357_18086657570559350_7034446288948897937_n.jpg"
                  alt="C.D. McKenna" className="h-11 w-11 rounded-full object-cover object-top" />
                <div className="leading-tight">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-[#A1A1AA]">Written by</div>
                  <div className="text-sm font-medium group-hover:text-[#C8A97E] transition-colors flex items-center gap-1">C.D. McKenna <ExternalLink size={12} /></div>
                </div>
              </a>
            ))}
          </div>
          <div className="lg:col-span-8">
            <div className="text-xs font-mono tracking-widest text-[#C8A97E] uppercase mb-4">{teasers.length} Marketing Teasers · Original Character Voices</div>
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {teasers.map((t, i) => (
                <Reveal key={t.id} delay={i * 0.05}>
                  <div className="rounded-lg border border-white/10 bg-[#12141A] overflow-hidden hover:border-[#C8A97E]/40 transition-all" data-testid={`featured-teaser-${t.id}`}>
                    <div className="bg-black flex justify-center">
                      <video src={resolveAudio(t.video_path)} controls playsInline preload="metadata" onPlay={(e) => stopAllMediaExcept(e.target)} className="max-h-[320px] w-auto max-w-full" />
                    </div>
                    <div className="p-3"><span className="text-sm font-medium">{t.title}</span></div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Section = ({ id, kicker, title, children, sub, titleExtra }) => (
  <section id={id} className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32">
    <Reveal>
      <div className="mb-14 max-w-2xl">
        <span className="font-mono text-xs tracking-[0.3em] text-[#C8A97E] uppercase">{kicker}</span>
        <div className="flex items-center gap-3 mt-3">
          <h2 className="font-head text-4xl md:text-5xl font-bold tracking-tight">{title}</h2>
          {titleExtra}
        </div>
        {sub && <p className="text-[#A1A1AA] mt-4 text-base leading-relaxed">{sub}</p>}
      </div>
    </Reveal>
    {children}
  </section>
);

export default function Home() {
  const [services, setServices] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [ba, setBa] = useState([]);
  const [cat, setCat] = useState("narration");
  const [content, setContent] = useState({});
  const [sections, setSections] = useState([]);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data));
    api.get("/portfolio").then((r) => setPortfolio(r.data));
    api.get("/before-after").then((r) => setBa(r.data));
    api.get("/site-content").then((r) => setContent(r.data)).catch(() => {});
    api.get("/site-sections").then((r) => setSections(r.data)).catch(() => {});
  }, []);

  const T = (k, d = "") => (content[k] != null && content[k] !== "" ? content[k] : d);
  const showSocials = String(T("show_socials", "true")) !== "false";
  const month = new Date().toLocaleString("default", { month: "long" });
  const secMeta = (id) => sections.find((s) => s.id === id);
  const Slot = ({ id, children }) => {
    const m = secMeta(id);
    if (m && m.visible === false) return null;
    return <div style={{ order: m ? m.order : 0 }}>{children}</div>;
  };

  const filtered = useMemo(() => portfolio.filter((p) => p.category === cat), [portfolio, cat]);
  const featuredBook = useMemo(() => portfolio.find((p) => p.category === "audiobook" && p.featured), [portfolio]);
  const featuredTeasers = useMemo(
    () => (featuredBook ? portfolio.filter((p) => p.category === "teaser" && p.client === featuredBook.title) : []),
    [portfolio, featuredBook]
  );

  return (
    <div className="grain relative min-h-screen">
      <Navbar />
      <QuoteDialog listenGlobal />

      {/* HERO */}
      <header className="relative min-h-[100vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={optimizeImg(HERO_BG, 1600)} alt="" fetchpriority="high" className="w-full h-full object-cover grayscale opacity-30" />
          <div className="absolute inset-0 bg-[#0B0C10]/85" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C10] via-transparent to-[#0B0C10]/60" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 w-full grid lg:grid-cols-12 gap-12 items-center pt-24">
          <motion.div className="lg:col-span-7" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <div className="flex items-center gap-2 mb-6">
              {[1,2,3,4,5].map(i => <Star key={i} size={14} className="text-[#C8A97E]" fill="#C8A97E" />)}
              <span className="text-xs text-[#A1A1AA] ml-2">Trusted by studios, authors & brands</span>
            </div>
            <div data-testid="availability-badge" className="inline-flex items-center gap-2.5 mb-6 pl-3 pr-4 py-1.5 rounded-full border border-[#C8A97E]/30 bg-[#C8A97E]/[0.07]">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
              </span>
              <span className="text-xs font-medium text-[#E8DCC8] tracking-wide">
                {T("hero_availability", "Now booking {month} — reply within 24h").replace("{month}", month)}
              </span>
            </div>
            <h1 className="font-head text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.02]">
              {T("hero_h1_line1", "Dynamic Voice.")}<br /><span className="text-gradient-gold">{T("hero_h1_line2", "Precision Engineering.")}</span>
            </h1>
            <div className="flex items-start gap-3 mt-6 max-w-2xl">
              <p className="text-[#A1A1AA] text-sm md:text-base leading-relaxed">
                {T("hero_bio", "Evan Osinchuk is an Edmonton-based voice actor, audiobook narrator, and audio engineer. Balancing deep, physical character work with strict technical discipline, providing creative directors, game developers, and authors with fully realized, retail-ready audio assets. Whether you need a raw, intimate vocal performance or a heavily stylized, sound-designed creature suite, his tracks are engineered to drop seamlessly into your project pipeline without requiring clean-up.")}
              </p>
              <HowItWorksButton testId="hiw-btn-hero" className="mt-1" />
            </div>
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4 mt-8">
              <Button onClick={() => document.getElementById("contact").scrollIntoView({ behavior: "smooth" })}
                data-testid="hero-hire" className="w-full sm:w-auto justify-center bg-[#C8A97E] text-[#0B0C10] hover:bg-[#B3956D] rounded-sm font-semibold h-12 px-7 text-base">
                Hire Me <ArrowRight size={18} className="ml-1.5" />
              </Button>
              <Button onClick={() => document.getElementById("fxlab").scrollIntoView({ behavior: "smooth" })}
                data-testid="hero-portfolio" variant="outline" className="w-full sm:w-auto justify-center border-white/20 bg-transparent hover:bg-white/5 hover:border-[#C8A97E]/50 rounded-sm h-12 px-7 text-base text-white">
                Hear My Work
              </Button>
              <a href="/EOVO-Rate-Card-2026.pdf" target="_blank" rel="noopener noreferrer" download
                data-testid="hero-rate-card"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 h-12 px-7 text-base rounded-sm border border-white/20 bg-transparent text-white hover:bg-white/5 hover:border-[#C8A97E]/50 transition-colors">
                <Download size={18} /> Rate Card
              </a>
            </div>
            {showSocials && (
              <div className="mt-8">
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#6b6b73] mb-3">Follow / Listen</div>
                <SocialLinks />
              </div>
            )}
          </motion.div>
          <motion.div className="lg:col-span-5 hidden lg:flex justify-center" initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, delay: 0.2 }}>
            <img src={LOGO_FULL} alt="E.O.VO logo" className="w-[78%] object-contain drop-shadow-2xl" />
          </motion.div>
        </div>
      </header>

      {/* DYNAMIC SECTIONS (order + visibility from /site-sections) */}
      <div className="flex flex-col">
      <Slot id="fxlab"><VoiceFXLab kicker={T("fxlab_kicker")} title={T("fxlab_title")} sub={T("fxlab_sub")} /></Slot>

      <Slot id="services"><Section id="services" kicker={T("services_kicker", "What I Do")} title={T("services_title", "Services")} titleExtra={<HowItWorksButton testId="hiw-btn-services" />} sub={T("services_sub", "From full audiobook production to viral social promos — pick a lane and request a tailored quote.")}>
        <div className="grid md:grid-cols-2 gap-5">
          {services.map((s, i) => {
            const Icon = ICONS[s.icon] || Mic;
            return (
              <Reveal key={s.key} delay={i * 0.08}>
                <div data-testid={`service-card-${s.key}`}
                  className="group relative overflow-hidden rounded-lg border border-white/10 bg-[#12141A] hover:border-[#C8A97E]/40 transition-all duration-300 hover:-translate-y-1">
                  <div className="h-44 overflow-hidden">
                    <img src={optimizeImg(s.image_url, 600)} alt={s.title} loading="lazy" decoding="async" className="w-full h-full object-cover grayscale group-hover:grayscale-0 opacity-70 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" />
                  </div>
                  <div className="p-7">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="h-10 w-10 rounded-md bg-[#C8A97E]/10 border border-[#C8A97E]/30 flex items-center justify-center shrink-0">
                        <Icon size={18} className="text-[#C8A97E]" />
                      </div>
                      <h3 className="font-head text-xl font-medium">{s.title}</h3>
                    </div>
                    <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">{s.blurb}</p>
                    {s.price_label && <div className="font-mono text-sm text-[#C8A97E] mb-4" data-testid={`service-price-${s.key}`}>{s.price_label}</div>}
                    <QuoteDialog serviceValue={s.title} trigger={
                      <button data-testid={`service-view-${s.key}`}
                        className="w-full inline-flex items-center justify-center gap-1.5 text-sm border border-[#C8A97E]/40 text-[#C8A97E] px-3 py-2.5 rounded-sm hover:bg-[#C8A97E] hover:text-[#0B0C10] transition-colors font-semibold">
                        Get a quote <ArrowRight size={14} />
                      </button>
                    } />
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </Section></Slot>

      <Slot id="featured"><FeaturedSpotlight book={featuredBook} teasers={featuredTeasers} kicker={T("featured_kicker", "Featured Project")} title={T("featured_title", "Manuscript to Marketing")} sub={T("featured_sub", "From immersive narration to custom social-media teasers — a complete look at an end-to-end fantasy audiobook production.")} /></Slot>

      <Slot id="portfolio"><Section id="portfolio" kicker={T("portfolio_kicker", "Listen")} title={T("portfolio_title", "Sample Voice Work")} sub={T("portfolio_sub", "A sample of audiobooks, narrations, character voices, music and read-along scripts.")}>
        <div className="flex flex-wrap gap-2 mb-8">
          {CATS.map((c) => (
            <button key={c.key} onClick={() => setCat(c.key)} data-testid={`portfolio-cat-${c.key}`}
              className={`px-4 py-2 text-sm rounded-sm border transition-all ${cat === c.key ? "bg-[#C8A97E] text-[#0B0C10] border-[#C8A97E] font-semibold" : "border-white/10 text-[#A1A1AA] hover:border-white/30"}`}>
              {c.label}
            </button>
          ))}
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          {filtered.length === 0 && <p className="text-[#A1A1AA]">No samples in this category yet.</p>}
          {filtered.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.05}>
              <div data-testid={`portfolio-item-${p.id}`} className="rounded-lg border border-white/10 bg-[#12141A] overflow-hidden hover:border-white/20 transition-all">
                {p.video_path && (
                  <div className="bg-black flex justify-center">
                    <video src={resolveAudio(p.video_path)} controls playsInline preload="metadata"
                      onPlay={(e) => stopAllMediaExcept(e.target)}
                      poster={p.cover_url ? optimizeImg(p.cover_url, 700) : undefined} data-testid={`video-${p.id}`}
                      className="max-h-[460px] w-auto max-w-full" />
                  </div>
                )}
                <div className="flex gap-4 p-4">
                  {!p.video_path && p.cover_url && <img src={optimizeImg(p.cover_url, 220)} alt={p.title} loading="lazy" decoding="async" className={`rounded-md object-cover shrink-0 ${p.links && p.links.length > 0 && !p.audio_path ? "h-28 w-20" : "h-20 w-20"}`} />}
                  <div className="min-w-0">
                    <h4 className="font-head text-base font-medium truncate">{p.title}</h4>
                    <p className="text-xs text-[#A1A1AA] mt-1 line-clamp-2">{p.description}</p>
                    {(p.project_type || p.client || p.duration) && (
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2">
                        {p.project_type && <span className="text-[10px] font-mono uppercase tracking-wider text-[#C8A97E] bg-[#C8A97E]/10 border border-[#C8A97E]/20 px-2 py-0.5 rounded-sm">{p.project_type}</span>}
                        {p.client && <span className="text-[11px] text-[#8b8b93]">Client: {p.client}</span>}
                        {p.duration && <span className="text-[11px] text-[#8b8b93] font-mono">{p.duration}</span>}
                      </div>
                    )}
                  </div>
                </div>
                {p.script_text && (
                  <div className="px-4 pb-3"><ScriptExcerpt text={p.script_text} testid={`script-${p.id}`} /></div>
                )}
                {p.audio_path && !p.video_path && (
                  <div className="px-4 pb-4"><AudioPlayer src={p.audio_path} compact testid={`player-${p.id}`} /></div>
                )}
                {((p.links && p.links.length > 0) || SHOW_REQUEST.includes(p.category)) && (
                  <div className="px-4 pb-4 flex flex-wrap gap-2">
                    {(p.links || []).map((l, idx) => (
                      <a key={l.url || l.label} href={l.url} target="_blank" rel="noopener noreferrer" data-testid={`link-${p.id}-${idx}`}
                        className="inline-flex items-center gap-1.5 text-xs bg-[#C8A97E] text-[#0B0C10] font-semibold px-3 py-1.5 rounded-sm hover:bg-[#B3956D] transition-colors">
                        <ExternalLink size={13} /> {l.label}
                      </a>
                    ))}
                    {SHOW_REQUEST.includes(p.category) && (
                      <QuoteDialog defaultService={`Voice sample: ${p.title}`} serviceValue={CATEGORY_SERVICE[p.category]} trigger={
                        <button data-testid={`request-voice-${p.id}`}
                          className="inline-flex items-center gap-1.5 text-xs border border-white/15 text-white px-3 py-1.5 rounded-sm hover:border-[#C8A97E]/60 hover:text-[#C8A97E] transition-colors">
                          <Mic size={13} /> Request this voice
                        </button>
                      } />
                    )}
                  </div>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </Section></Slot>

      <Slot id="mastering"><section id="mastering" className="border-y border-white/5 bg-[#0d0e13]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32">
          <Reveal>
            <div className="mb-12 max-w-2xl">
              <span className="font-mono text-xs tracking-[0.3em] text-[#C8A97E] uppercase">{T("mastering_kicker", "Mastering")}</span>
              <h2 className="font-head text-4xl md:text-5xl font-bold tracking-tight mt-3">{T("mastering_title", "Before / After")}</h2>
              <p className="text-[#A1A1AA] mt-4 leading-relaxed">{T("mastering_sub", "Toggle between the raw recording and the finished master — same moment, transformed.")}</p>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-5">
            {ba.map((item, i) => <Reveal key={item.id} delay={i * 0.08}><BeforeAfter item={item} /></Reveal>)}
          </div>
        </div>
      </section></Slot>

      <Slot id="music"><MusicSection /></Slot>

      <Slot id="contact"><section id="contact" className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <Reveal>
              <span className="font-mono text-xs tracking-[0.3em] text-[#C8A97E] uppercase">{T("contact_kicker", "Let's Talk")}</span>
              <h2 className="font-head text-4xl md:text-5xl font-bold tracking-tight mt-3">{T("contact_title", "Start your project")}</h2>
              <div className="flex items-start gap-3 mt-4 max-w-lg">
                <p className="text-[#A1A1AA] leading-relaxed">
                  {T("contact_sub", "Have a script, a track to master, or a character that needs a voice? Send the details and I'll reply within 24 hours with a tailored quote.")}
                </p>
                <HowItWorksButton testId="hiw-btn-contact" className="mt-1" />
              </div>
              <div className="mt-8 space-y-4">
                <div className="flex items-center gap-3 text-[#D4D4D8]"><Mail size={18} className="text-[#C8A97E]" /> contact@eovo.pro</div>
                <div className="flex items-center gap-3 text-[#D4D4D8]"><MapPin size={18} className="text-[#C8A97E]" /> Remote · Worldwide delivery</div>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <QuoteDialog trigger={<Button data-testid="contact-quote" className="bg-[#C8A97E] text-[#0B0C10] hover:bg-[#B3956D] rounded-sm font-semibold h-12 px-8 text-base">Request a Quote <ArrowRight size={18} className="ml-1.5" /></Button>} />
                <a href="/EOVO-Rate-Card-2026.pdf" target="_blank" rel="noopener noreferrer" download
                  data-testid="contact-rate-card"
                  className="inline-flex items-center gap-2 h-12 px-6 text-base rounded-sm border border-white/20 text-white hover:bg-white/5 hover:border-[#C8A97E]/50 transition-colors">
                  <Download size={18} /> Download Rate Card
                </a>
              </div>
              {showSocials && <div className="mt-8"><SocialLinks testidPrefix="contact-social" /></div>}
            </Reveal>
            <Reveal delay={0.1}>
              <div className="rounded-xl border border-white/10 bg-[#12141A] p-8">
                <h4 className="font-head text-xl mb-5">Why work with E.O.VO</h4>
                <ul className="space-y-4">
                  {["Broadcast-quality home studio", "Fast turnaround & clear communication", "Commercial & broadcast licensing", "One artist for voice, mastering & production"].map((t) => (
                    <li key={t} className="flex items-center gap-3 text-[#D4D4D8]"><span className="h-1.5 w-1.5 rounded-full bg-[#C8A97E]" /> {t}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section></Slot>
      </div>

      {/* FOOTER */}
      <footer className="border-t border-white/10 py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_sonic-pro-1/artifacts/x1yk97dm_EOVO-Logo-Transparent-NoText-Upscaled.png" alt="" className="h-9 w-9 object-contain" />
            <span className="font-head font-bold tracking-[0.2em]">E.O.VO</span>
          </div>
          <p className="text-xs text-[#A1A1AA]">© {new Date().getFullYear()} E.O.VO — Evan Osinchuk. Voice • Narration • Audio.</p>
          <a href="/admin/login" data-testid="footer-admin" className="text-xs text-[#6b6b73] hover:text-[#C8A97E] transition-colors">Admin</a>
        </div>
      </footer>
    </div>
  );
}
